<script setup lang="ts">
import type { PageSection } from "@/apps/page/page.interfaces";
import {
    BlockDefinition,
    BlockDefinitionGroup,
    BlockDefWithType,
    BlockTypeGroup,
} from "@/apps/page/services/blocks/block.interfaces";
import { useBlocks } from "../../../../services/blocks/useBlocks";
import { useFocusOnEditSection } from "../../../../services/useFocusOnEditSection";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { BlockBaseElement, BlockDataValue } from "@/views/blocks/PageBlock.interface";
import { ButtonSecondaryIconSm, ButtonTertiaryIconXs } from "@easy/ui/buttons";
import { CardToggle, CardToggleAlt } from "@easy/ui/cards";
import {
    FormInput,
    FormInputQuantity,
    FormLabel,
    FormRichText,
    FormSelect,
    FormSwitch,
} from "@easy/ui/forms";
import { ArrowLeftIcon, InvisibleIcon, VisibleIcon } from "@easy/ui/icons";
import { PageSidebar } from "@easy/ui/page-components";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import BlockImage from "./BlockImage.vue";
import GroupItems from "./GroupItems.vue";
import PageColors from "@/apps/page/project/sidebar/sections/PageColors.vue";
import { ThemeColors } from "@/shared/themes/Theme.interface";

const { section } = defineProps<{
    section: PageSection;
}>();

const { getBlockWithTypeMetadata, groupTypes } = useBlocks();
const blockWithMetadata = computed<BlockDefWithType | null>(() =>
    section.typeId ? getBlockWithTypeMetadata(section.typeId, section.type) : null
);

const emit = defineEmits(["saveBlock", "cancelBlock"]);

const groupedDataFields = computed(() => {
    return groupTypes.value.reduce<
        Record<BlockDefinitionGroup, BlockDefinition<BlockBaseElement<BlockDataValue>>[]>
    >(
        (
            acc: Record<BlockDefinitionGroup, BlockDefinition<BlockBaseElement<BlockDataValue>>[]>,
            type: BlockTypeGroup
        ) => {
            const fields =
                blockWithMetadata.value?.block?.dataFields.filter(
                    (field) => field.group === type.group
                ) ?? [];

            acc[type.group] = fields;

            return acc;
        },
        {} as Record<BlockDefinitionGroup, BlockDefinition<BlockBaseElement<BlockDataValue>>[]>
    );
});

const { t } = useI18n();

const saveBlock = async () => {
    if (!section._created) {
        section._changed = true;
    }
    emit("saveBlock");
};

const saveButtonText = computed(() => t("shared.save"));

const tabs = computed(() => [
    {
        label: t("page.block.content"),
        value: "content",
    },
    {
        label: t("page.block.styles"),
        value: "styles",
    },
]);

const selectedTab = ref("content");

useFocusOnEditSection();
</script>

<template>
    <PageSidebar>
        <template #sidebarTitle>
            <div class="flex items-center space-x-3">
                <ButtonSecondaryIconSm @click="emit('cancelBlock')">
                    <ArrowLeftIcon />
                </ButtonSecondaryIconSm>
                <span class="text-lg truncate">
                    {{ t("page.sections.edit_section") }} -
                    {{ blockWithMetadata?.blockType.title }}
                </span>
            </div>
        </template>
        <template #sidebarMain>
            <div class="p-6">
                <FormSwitch v-model="selectedTab" :options="tabs" />
            </div>
            <template v-if="selectedTab === 'content'">
                <template v-for="groupType in groupTypes" :key="groupType.group">
                    <template v-if="groupedDataFields[groupType.group].length > 0">
                        <CardToggle :title="groupType.name">
                            <div class="flex flex-col gap-2">
                                <template
                                    v-for="groupedField in groupedDataFields[groupType.group]"
                                    :key="groupedField.value"
                                >
                                    <template v-if="groupedField.groupType === 'multiple'">
                                        <GroupItems
                                            :grouped-field="groupedField"
                                            :section="section"
                                            :group-icon="groupType.icon"
                                        />
                                    </template>
                                    <CardToggleAlt
                                        v-if="groupedField.groupType === 'single'"
                                        :data-field="groupedField.value"
                                        :title="groupedField.label"
                                        :icon="groupType.icon"
                                        :class="{
                                            'opacity-50':
                                                section.dataValues[groupedField.value].isHidden,
                                        }"
                                    >
                                        <template #default>
                                            <div class="rounded-md space-y-3 pb-3">
                                                <div
                                                    v-for="field in groupedField.fields"
                                                    :key="field.value"
                                                >
                                                    <FormInput
                                                        :id="field.value"
                                                        v-if="field.type === 'text'"
                                                        :title="field.label"
                                                        v-model="
                                                            section.dataValues[groupedField.value]
                                                                .data[
                                                                field.value as keyof BlockDataValue
                                                            ]
                                                        "
                                                    />
                                                    <FormSelect
                                                        v-if="field.type === 'select'"
                                                        v-model="
                                                            section.dataValues[groupedField.value]
                                                                .data[
                                                                field.value as keyof BlockDataValue
                                                            ]
                                                        "
                                                        :label="field.label"
                                                        :options="field.options"
                                                        :value="
                                                            section.dataValues[groupedField.value]
                                                                .data[
                                                                field.value as keyof BlockDataValue
                                                            ]
                                                        "
                                                    />
                                                    <FormRichText
                                                        :id="field.value"
                                                        v-if="field.type === 'html'"
                                                        :title="field.label"
                                                        v-model="
                                                            section.dataValues[groupedField.value]
                                                                .data[
                                                                field.value as keyof BlockDataValue
                                                            ]
                                                        "
                                                        :rows="field.rows"
                                                    />
                                                    <BlockImage
                                                        :id="field.value"
                                                        v-if="field.type === 'image'"
                                                        :title="field.label"
                                                        v-model="
                                                            section.dataValues[groupedField.value]
                                                                .data[
                                                                field.value as keyof BlockDataValue
                                                            ]
                                                        "
                                                    />
                                                    <div
                                                        v-if="field.type === 'slider'"
                                                        class="flex flex-col gap-2"
                                                    >
                                                        <FormLabel>
                                                            {{ field.label }}
                                                        </FormLabel>
                                                        <div class="max-w-max">
                                                            <FormInputQuantity
                                                                :title="field.label"
                                                                v-model="
                                                                    section.dataValues[
                                                                        groupedField.value
                                                                    ].data[
                                                                        field.value as keyof BlockDataValue
                                                                    ]
                                                                "
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                        <template #actions>
                                            <ButtonTertiaryIconXs
                                                @click="
                                                    section.dataValues[
                                                        groupedField.value
                                                    ].isHidden =
                                                        !section.dataValues[groupedField.value]
                                                            .isHidden
                                                "
                                            >
                                                <VisibleIcon
                                                    v-if="
                                                        !section.dataValues[groupedField.value]
                                                            .isHidden
                                                    "
                                                />
                                                <InvisibleIcon v-else />
                                            </ButtonTertiaryIconXs>
                                        </template>
                                    </CardToggleAlt>
                                </template>
                            </div>
                        </CardToggle>
                    </template>
                </template>
            </template>
            <div v-if="selectedTab === 'styles'" class="space-y-5 p-6">
                <div v-for="field in blockWithMetadata?.block?.styleFields" :key="field.value">
                    <PageColors
                        v-if="field.type === 'color'"
                        :title="field.name"
                        v-model="section.styleValues[field.value as keyof ThemeColors]"
                    />
                </div>
            </div>
        </template>
        <template #sidebarFooter>
            <cSaveButton
                @save="saveBlock"
                :ctaEnabled="saveButtonText"
                :ctaDisabled="saveButtonText"
            />
        </template>
    </PageSidebar>
</template>
